import React from 'react';
import { 
  GiftIcon, 
  SparklesIcon,
  EyeIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

/**
 * BundleCard - Mobile-First Bundle Display
 * Shows bundle with pricing, discount, and quick actions
 */
const BundleCard = ({ bundle, onViewDetails, onAddToCart, disabled }) => {
  const regularPrice = parseFloat(bundle.regular_price || 0);
  const bundlePrice = parseFloat(bundle.bundle_price || 0);
  const savings = regularPrice - bundlePrice;
  const discountPercent = regularPrice > 0 ? ((savings / regularPrice) * 100).toFixed(0) : 0;

  // Get main image
  let imgSrc = 'https://via.placeholder.com/80?text=Bundle';
  if (bundle.images && Array.isArray(bundle.images) && bundle.images.length > 0) {
    imgSrc = bundle.images[0];
  } else if (bundle.bundle_image) {
    imgSrc = bundle.bundle_image;
  }

  // Check if bundle is valid (active and within date range)
  const isValid = bundle.is_active && 
    (!bundle.valid_from || new Date(bundle.valid_from) <= new Date()) &&
    (!bundle.valid_until || new Date(bundle.valid_until) >= new Date());

  // Check stock
  const hasStock = !bundle.track_quantity || bundle.stock_quantity > 0;

  return (
    <div className={`
      bg-white rounded-lg border-2 transition-all
      ${isValid && hasStock ? 'border-purple-300 shadow-sm hover:shadow-md' : 'border-gray-200 opacity-60'}
    `}>
      <div className="p-4">
        {/* Bundle Badge */}
        <div className="flex items-center gap-2 mb-3">
          <div className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full">
            <GiftIcon className="h-4 w-4 text-purple-600" />
            <span className="text-xs font-bold text-purple-800">חבילה</span>
          </div>
          {bundle.is_featured && (
            <div className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-yellow-100 to-orange-100 rounded-full">
              <SparklesIcon className="h-4 w-4 text-orange-600" />
              <span className="text-xs font-bold text-orange-800">מומלץ</span>
            </div>
          )}
          {!isValid && (
            <span className="text-xs text-red-600 font-medium">לא פעיל</span>
          )}
          {!hasStock && (
            <span className="text-xs text-red-600 font-medium">אזל מהמלאי</span>
          )}
        </div>

        <div className="flex gap-4">
          {/* Bundle Image */}
          <div className="flex-shrink-0">
            <div className="relative">
              <img
                src={imgSrc}
                alt={bundle.title}
                className="w-20 h-20 object-cover rounded-lg"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/80?text=Bundle'; }}
              />
              {discountPercent > 0 && (
                <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                  -{discountPercent}%
                </div>
              )}
            </div>
          </div>

          {/* Bundle Info */}
          <div className="flex-1 min-w-0">
            {/* Title */}
            <h3 className="font-bold text-gray-900 mb-1 line-clamp-2">
              {bundle.title}
            </h3>

            {/* Description */}
            {bundle.description && (
              <p className="text-xs text-gray-600 mb-2 line-clamp-1">
                {bundle.description}
              </p>
            )}

            {/* Item Count */}
            {bundle.bundle_items && bundle.bundle_items.length > 0 && (
              <div className="text-xs text-gray-500 mb-2">
                📦 {bundle.bundle_items.length} פריטים בחבילה
              </div>
            )}

            {/* Pricing */}
            <div className="mb-3">
              <div className="flex items-center gap-2">
                {regularPrice > bundlePrice && (
                  <span className="text-sm text-gray-400 line-through">
                    ₪{regularPrice.toFixed(2)}
                  </span>
                )}
                <span className="text-xl font-bold text-purple-600">
                  ₪{bundlePrice.toFixed(2)}
                </span>
              </div>
              {savings > 0 && (
                <div className="text-xs text-green-600 font-semibold mt-1">
                  💰 חיסכון של ₪{savings.toFixed(2)}
                </div>
              )}
            </div>

            {/* Validity Period */}
            {bundle.valid_until && (
              <div className="text-xs text-gray-500 mb-3">
                🕒 תקף עד: {new Date(bundle.valid_until).toLocaleDateString('he-IL')}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => onViewDetails(bundle)}
                className="flex-1 py-2 px-3 border-2 border-purple-300 text-purple-700 rounded-lg font-medium hover:bg-purple-50 transition-colors flex items-center justify-center gap-2"
              >
                <EyeIcon className="h-4 w-4" />
                <span className="text-sm">פרטים</span>
              </button>
              <button
                onClick={() => onAddToCart(bundle)}
                disabled={disabled || !isValid || !hasStock}
                className="flex-1 py-2 px-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <PlusIcon className="h-4 w-4" />
                <span className="text-sm">הוסף</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BundleCard;

